
import { useState } from 'react';
import HologramCard from '../../../components/base/HologramCard';
import GlitchText from '../../../components/base/GlitchText';

export default function SkillsSection() {
  const [activeCategory, setActiveCategory] = useState('product');

  const skillCategories = {
    product: {
      title: "PRODUCT STRATEGY",
      icon: "ri-lightbulb-line",
      color: "cyan",
      skills: [
        { name: "Product Strategy", icon: "ri-compass-line" },
        { name: "OKRs & KPIs", icon: "ri-target-line" },
        { name: "Feature Prioritization", icon: "ri-sort-desc" },
        { name: "Roadmapping", icon: "ri-map-line" },
        { name: "User Research", icon: "ri-user-search-line" },
        { name: "Metrics Thinking", icon: "ri-bar-chart-line" }
      ]
    },
    technical: {
      title: "TECHNICAL STACK",
      icon: "ri-code-line",
      color: "green",
      skills: [
        { name: "SQL & Databases", icon: "ri-database-line" },
        { name: "Python", icon: "ri-file-code-line" },
        { name: "Dart & Flutter", icon: "ri-smartphone-line" },
        { name: "Java & Kotlin", icon: "ri-java-line" },
        { name: "Git & Version Control", icon: "ri-git-branch-line" },
        { name: "API Integration", icon: "ri-plug-line" }
      ]
    },
    tools: {
      title: "TOOLS & PLATFORMS",
      icon: "ri-tools-line",
      color: "orange",
      skills: [
        { name: "Jira & Agile", icon: "ri-kanban-view" },
        { name: "Figma & Design", icon: "ri-palette-line" },
        { name: "Analytics Tools", icon: "ri-line-chart-line" },
        { name: "A/B Testing", icon: "ri-test-tube-line" },
        { name: "Slack & Communication", icon: "ri-chat-3-line" },
        { name: "Documentation", icon: "ri-file-text-line" }
      ]
    },
    soft: {
      title: "SOFT SKILLS",
      icon: "ri-team-line",
      color: "purple",
      skills: [
        { name: "Cross-functional Leadership", icon: "ri-group-line" },
        { name: "Stakeholder Management", icon: "ri-handshake-line" },
        { name: "Problem Solving", icon: "ri-puzzle-line" },
        { name: "Communication", icon: "ri-speak-line" },
        { name: "Decision Making", icon: "ri-checkbox-circle-line" },
        { name: "Adaptability", icon: "ri-refresh-line" }
      ]
    }
  };

  return (
    <section id="skills" className="min-h-screen py-20 bg-gradient-to-b from-gray-900 to-black">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-white font-orbitron mb-4">
            <GlitchText text="GPU MATRIX" />
          </h2>
          <div className="text-xl text-green-400 font-mono mb-2">GRAPHICS PROCESSING CAPABILITIES</div>
          <p className="text-gray-400 font-rajdhani text-lg">Parallel processing power and specialized functions</p>
        </div>

        {/* Category Selector */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {Object.entries(skillCategories).map(([key, category]) => (
            <button
              key={key}
              onClick={() => setActiveCategory(key)}
              className={`
                px-6 py-3 rounded-lg font-mono text-sm transition-all duration-300
                flex items-center space-x-2 border-2
                ${activeCategory === key 
                  ? `bg-${category.color}-500/20 text-${category.color}-400 border-${category.color}-500/50 shadow-lg shadow-${category.color}-500/30` 
                  : 'text-gray-300 border-gray-600 hover:border-gray-400'
                }
              `}
            >
              <i className={`${category.icon} text-lg`}></i>
              <span>{category.title}</span>
            </button>
          ))}
        </div>

        {/* Skills Display */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {skillCategories[activeCategory as keyof typeof skillCategories].skills.map((skill, index) => (
            <HologramCard 
              key={skill.name} 
              glowColor={skillCategories[activeCategory as keyof typeof skillCategories].color}
              className="animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` } as React.CSSProperties}
            >
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <i className={`${skill.icon} text-2xl text-${skillCategories[activeCategory as keyof typeof skillCategories].color}-400`}></i>
                  <h3 className="text-lg font-bold text-white font-orbitron">{skill.name}</h3>
                </div>

                {/* Status Indicator */}
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-xs text-green-400 font-mono">ACTIVE</span>
                </div>
              </div>
            </HologramCard>
          ))}
        </div>
      </div>
    </section>
  );
}
